/**
 * Platform Admin Dashboard
 *
 * Overview page showing all organizations in the platform.
 * Includes stats cards and organizations table.
 */

import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { usePlatformOrganizations } from '../../hooks/queries';
import { DataFreshness } from '../../components/ui/DataFreshness';
import OrganizationsTable from './OrganizationsTable';
import CreateOrganizationModal from './CreateOrganizationModal';
import { PlatformOrganization } from '../../api/platform';

export default function PlatformDashboard() {
  const navigate = useNavigate();
  const [showCreateModal, setShowCreateModal] = useState(false);
  const {
    organizations,
    total,
    loading,
    error,
    isRefetching,
    dataUpdatedAt,
    refetch,
  } = usePlatformOrganizations();

  // Calculate stats
  const activeCount = organizations.filter((o) => o.isActive).length;
  const tierCounts = organizations.reduce(
    (acc, org) => {
      acc[org.subscriptionTier] = (acc[org.subscriptionTier] || 0) + 1;
      return acc;
    },
    {} as Record<string, number>
  );

  const handleRowClick = (org: PlatformOrganization) => {
    navigate(`/platform/organizations/${org.id}`);
  };

  return (
    <div>
      {/* Page header */}
      <div className="mb-8 flex items-start justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Platform Dashboard</h1>
          <p className="text-gray-600 mt-1">
            Manage all organizations across the platform.
          </p>
        </div>
        <div className="flex items-center space-x-3">
          <DataFreshness
            dataUpdatedAt={dataUpdatedAt}
            onRefresh={refetch}
            isRefreshing={isRefetching}
          />
          <button
            onClick={() => setShowCreateModal(true)}
            className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-primary-600 hover:bg-primary-700 transition-colors"
          >
            + New Organization
          </button>
        </div>
      </div>

      <CreateOrganizationModal
        isOpen={showCreateModal}
        onClose={() => setShowCreateModal(false)}
        onCreated={() => refetch()}
      />

      {/* Error state */}
      {error && (
        <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-lg text-red-700">
          {error}
        </div>
      )}

      {/* Stats grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
        <StatCard
          title="Total Organizations"
          value={String(total)}
          loading={loading}
        />
        <StatCard
          title="Active"
          value={String(activeCount)}
          loading={loading}
        />
        <StatCard
          title="Pro Tier"
          value={String(tierCounts.PRO || 0)}
          loading={loading}
        />
        <StatCard
          title="Enterprise Tier"
          value={String(tierCounts.ENTERPRISE || 0)}
          loading={loading}
        />
      </div>

      {/* Organizations table */}
      <div>
        <h2 className="text-lg font-semibold text-gray-900 mb-4">
          All Organizations
        </h2>
        <OrganizationsTable
          organizations={organizations}
          loading={loading}
          onRowClick={handleRowClick}
        />
      </div>
    </div>
  );
}

// =============================================================================
// Sub-components
// =============================================================================

interface StatCardProps {
  title: string;
  value: string;
  loading?: boolean;
}

/** Matches the org admin dashboard: a number under a graphite rail, no icon tile. */
function StatCard({ title, value, loading }: StatCardProps) {
  return (
    <div className="bg-white border border-hairline border-t-[3px] border-t-rack p-6">
      <h3 className="text-sm font-medium text-ink-body">{title}</h3>
      {loading ? (
        <div className="mt-2 h-[42px] flex items-center">
          <div className="w-6 h-6 border-2 border-rack border-t-transparent rounded-full animate-spin" />
        </div>
      ) : (
        <p className="mt-2 text-[42px] leading-none font-semibold text-ink">{value}</p>
      )}
    </div>
  );
}
